import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PrinterFilters = ({ 
  searchTerm, 
  onSearchChange, 
  sortBy, 
  onSortChange, 
  statusFilter, 
  onStatusFilterChange,
  brandFilter = [],
  onBrandFilterChange,
  availableBrands = [],
  modelFilter = [],
  onModelFilterChange,
  availableModels = [],
  operationFilter,
  onOperationFilterChange,
  amsFilter,
  onAmsFilterChange,
  nozzleFilter = [],
  onNozzleFilterChange,
  availableNozzles = [],
  operationBasisFilter,
  onOperationBasisFilterChange,
  periodType,
  onPeriodTypeChange,
  periodStart,
  periodEnd,
  onPeriodStartChange,
  onPeriodEndChange,
  onClearFilters,
  totalCount,
  filteredCount 
}) => {
  const sortOptions = [
    { value: 'name', label: 'Nome A-Z' },
    { value: 'name-desc', label: 'Nome Z-A' },
    { value: 'hours', label: 'Mais Usadas' },
    { value: 'hours-asc', label: 'Menos Usadas' },
    { value: 'recent', label: 'Adicionadas Recentemente' },
    { value: 'oldest', label: 'Mais Antigas' }
  ];

  const statusOptions = [
    { value: 'all', label: 'Todos' },
    { value: 'active', label: 'Ativas' },
    { value: 'idle', label: 'Ociosas' },
    { value: 'maintenance', label: 'Manutenção' },
    { value: 'inactive', label: 'Fora de serviço' },
    { value: 'nonexistent', label: 'Inexistente' }
  ];

  const periodOptions = [
    { value: 'current_month', label: 'Mês atual' },
    { value: 'previous_month', label: 'Mês anterior' },
    { value: 'last_30d', label: 'Últimos 30 dias' },
    { value: 'last_90d', label: 'Últimos 90 dias' },
    { value: 'all_time', label: 'Todo o Período' },
    { value: 'custom', label: 'Personalizado' }
  ];

  const formatDateBR = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (!isNaN(d)) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = String(d.getFullYear());
        return `${dd}/${mm}/${yyyy}`;
      }
      const m = dateStr.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const hasActiveFilters = Boolean(
    (searchTerm && searchTerm.trim().length > 0) ||
    (statusFilter && statusFilter !== 'all') ||
    (Array.isArray(brandFilter) && brandFilter.length > 0) ||
    (Array.isArray(modelFilter) && modelFilter.length > 0) ||
    (amsFilter && amsFilter !== 'all') ||
    (Array.isArray(nozzleFilter) && nozzleFilter.length > 0)
  );

  // Fallback de marcas sugeridas quando não houver dados das impressoras
  const defaultBrandOptions = ['Creality', 'Prusa Research', 'Bambu Lab', 'Anycubic', 'Elegoo']
    .map(b => ({ value: b, label: b }));
  const brandOptions = (availableBrands && availableBrands.length > 0)
    ? [...availableBrands].sort((a, b) => a?.localeCompare(b)).map(b => ({ value: b, label: b }))
    : defaultBrandOptions;

  // Removemos filtros que não fazem sentido para simplificar a UX

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-foreground">Filtros</h2>
          <div className="text-sm text-muted-foreground">
            {filteredCount || 0} de {totalCount || 0} impressoras
          </div>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} className="mr-2" />
            Limpar Filtros
          </Button>
        )}
      </div>
      <div className="space-y-4">
        {/* Nome */}
        <div className="lg:max-w-md">
          <Input
            label="Nome"
            type="search"
            placeholder="Buscar por nome da impressora"
            value={searchTerm}
            onChange={(e) => onSearchChange(e?.target?.value)}
            startIconName="Search"
          />
        </div>

        {/* Linha de filtros */}
        <div className="flex flex-wrap items-end gap-3">
          {/* Período */}
          <Select
            label="Período"
            options={periodOptions}
            value={periodType}
            onChange={onPeriodTypeChange}
            className="min-w-[200px] w-full sm:w-auto"
          />
          {periodType === 'custom' && (
            <>
              <Input
                label="Início"
                type="date"
                value={periodStart}
                onChange={(e) => onPeriodStartChange(e?.target?.value)}
                className="min-w-[180px] w-full sm:w-auto"
                lang="pt-BR"
                placeholder="dd/mm/aaaa"
              />
              <Input
                label="Fim"
                type="date"
                value={periodEnd}
                onChange={(e) => onPeriodEndChange(e?.target?.value)}
                className="min-w-[180px] w-full sm:w-auto"
                lang="pt-BR"
                placeholder="dd/mm/aaaa"
              />
            </>
          )}
          <Select
            label={`Marca (${(availableBrands || []).length})`}
            multiple
            searchable
            clearable
            options={brandOptions}
            value={brandFilter}
            onChange={onBrandFilterChange}
            className="min-w-[220px] w-full sm:w-auto"
          />

          <Select
            label={`Modelo (${(availableModels || []).length})`}
            multiple
            searchable
            clearable
            options={[...(availableModels || [])].sort((a, b) => a?.localeCompare(b)).map(m => ({ value: m, label: m }))}
            value={modelFilter}
            onChange={onModelFilterChange}
            className="min-w-[220px] w-full sm:w-auto"
          />

          <Select
            label="Bico"
            multiple
            clearable
            options={(availableNozzles || []).map(n => ({ value: String(n), label: `${n} mm` }))}
            value={nozzleFilter}
            onChange={onNozzleFilterChange}
            className="min-w-[200px] w-full sm:w-auto"
          />

          <Select
            label="Status"
            options={statusOptions}
            value={statusFilter}
            onChange={onStatusFilterChange}
            className="min-w-[200px] w-full sm:w-auto"
          />

          <Select
            label="AMS"
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'yes', label: 'Com AMS' },
              { value: 'no', label: 'Sem AMS' }
            ]}
            value={amsFilter}
            onChange={onAmsFilterChange}
            className="min-w-[180px] w-full sm:w-auto"
          />
        </div>
      </div>
      {/* Filter Summary */}
      {hasActiveFilters && (
        <div className="mb-6 p-4 bg-muted/30 rounded-xl border border-border/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-foreground">Filtros Ativos</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={12} className="mr-1" />
              Limpar Todos
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Período ativo */}
            {periodType && (
              <button
                className="flex items-center space-x-1 px-3 py-1 bg-muted text-muted-foreground rounded-full text-xs hover:bg-muted/80"
                title="Alterar período"
                onClick={() => onPeriodTypeChange(periodType === 'all_time' ? 'current_month' : 'all_time')}
              >
                <Icon name="Calendar" size={12} />
                <span>
                  {periodOptions?.find(p => p?.value === periodType)?.label}
                  {periodType === 'custom' && `: ${formatDateBR(periodStart)} a ${formatDateBR(periodEnd)}`}
                </span>
                <Icon name="ChevronDown" size={12} />
              </button>
            )}
            {searchTerm && (
              <div className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                <Icon name="Search" size={12} />
                <span>"{searchTerm}"</span>
                <button
                  onClick={() => onSearchChange('')}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            )}
            
            {/* Ordenação removida da visualização dos filtros */}
            
            {statusFilter !== 'all' && (
              <div className="flex items-center space-x-1 px-3 py-1 bg-accent/10 text-accent rounded-full text-sm">
                <Icon name="Filter" size={12} />
                <span>{statusOptions?.find(opt => opt?.value === statusFilter)?.label}</span>
                <button
                  onClick={() => onStatusFilterChange('all')}
                  className="ml-1 hover:bg-accent/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            )}

            {brandFilter?.map(brand => (
              <div key={brand} className="flex items-center space-x-1 px-3 py-1 bg-black/50 text-white border border-white/30 shadow-xs backdrop-blur-sm rounded-full text-sm">
                <Icon name="Tag" size={12} />
                <span>{brand}</span>
                <button
                  onClick={() => onBrandFilterChange(brandFilter?.filter(b => b !== brand))}
                  className="ml-1 hover:bg-white/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            ))}

            {modelFilter?.map(model => (
              <div key={model} className="flex items-center space-x-1 px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm">
                <span>{model}</span>
                <button
                  onClick={() => onModelFilterChange(modelFilter?.filter(m => m !== model))}
                  className="ml-1 hover:bg-secondary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            ))}

            {/* Base de operação e operação removidos por simplicidade */}

            {amsFilter !== 'all' && (
              <div className="flex items-center space-x-1 px-3 py-1 bg-warning/10 text-warning rounded-full text-sm">
                <Icon name="Layers" size={12} />
                <span>{amsFilter === 'yes' ? 'Com AMS' : 'Sem AMS'}</span>
                <button
                  onClick={() => onAmsFilterChange('all')}
                  className="ml-1 hover:bg-warning/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            )}

            {nozzleFilter?.map(nozzle => (
              <div key={nozzle} className="flex items-center space-x-1 px-3 py-1 bg-success/10 text-success rounded-full text-sm">
                <span>{nozzle} mm</span>
                <button
                  onClick={() => onNozzleFilterChange(nozzleFilter?.filter(n => n !== nozzle))}
                  className="ml-1 hover:bg-success/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PrinterFilters;